import {
  BanIcon,
  ClockIcon,
  CloudIcon,
  CollectionIcon,
  ConvertCardIcon,
  Data2Icon,
  ExternalLinkIcon,
  OptimizationIcon,
  PauseIcon,
  PayInProviderIcon,
  PaymentProviderIcon,
  ReceiptRefundIcon,
  RefreshIcon,
  ReplyIcon,
  SecurityCardIcon,
  VoidPaymentIcon,
  XIcon,
} from "icons/outline";
import { Node } from "../types";

export type FETriggerType =
  | "new_card_collection"
  | "merchant_stored_data"
  | "moneyhash_stored_data"
  | null;

export const TriggerResourceType = {
  trigger: "Um91dGluZ1J1bGVUcmlnZ2Vy",
} as const;

export const ConditionResourceType = {
  amount: "QW1vdW50Q29uZGl0aW9u",
  currency: "Q3VycmVuY3lDb25kaXRpb24=",
  customCondition: "Q3VzdG9tRmllbGRzQ29uZGl0aW9u",
  country: "Q291bnRyeUNvbmRpdGlvbg==",
  error: "RXJyb3JDb25kaXRpb24=",
  binCode: "QmluQ29uZGl0aW9u",
  cardBrand: "Q2FyZEJyYW5kQ29uZGl0aW9u",
  cardCountry: "Q2FyZENvdW50cnlDb25kaXRpb24=",
  cardType: "Q2FyZFR5cGVDb25kaXRpb24=",
  metaData: "TWV0YURhdGFDb25kaXRpb24=",
  riskLevel: "Umlza0NvbmRpdGlvbg==",
  declineType: "RGVjbGluZUNvbmRpdGlvbg==",
  paymentType: "UGF5bWVudFR5cGVDb25kaXRpb24=",
  walletNumbers: "V2FsbGV0TnVtYmVyc0NvbmRpdGlvbg==",

  merchantInitiated: "TWVyY2hhbnRJbml0aWF0ZWRDb25kaXRpb24=",
  tokenPresent: "Q2FyZFRva2VuQ29uZGl0aW9u",

  http: "SHR0cFJlcXVlc3RDb25kaXRpb24=",
  methods: "TWV0aG9kU2VsZWN0b3JDb25kaXRpb24=",
} as const;

export type BEConditionResourceType =
  (typeof ConditionResourceType)[keyof typeof ConditionResourceType];
export type FEConditionResourceType = keyof typeof ConditionResourceType;

export const ActionResourceType = {
  provider: "UHJvdmlkZXJSb3V0aW5nQWN0aW9u",
  capture: "Q2FwdHVyZVJvdXRpbmdBY3Rpb24=",
  void: "Vm9pZFJvdXRpbmdBY3Rpb24=",
  block: "QmxvY2tBY3Rpb24=",
  close: "Q2xvc2VJbnRlbnRBY3Rpb24=",
  refund: "UmVmdW5kUm91dGluZ0FjdGlvbg==",
  branch: "Q29uZGl0aW9uYWxCcmFuY2hSb3V0aW5nQWN0aW9u",
  recovery: "UmVjb3ZlcnlSb3V0aW5nQWN0aW9u",
  delay: "RGVsYXlSb3V0aW5nQWN0aW9u",
  deviceCheck: "RGV2aWNlQ2hlY2tBY3Rpb24=",
  transition: "VHJhbnNpdGlvblJvdXRpbmdBY3Rpb24=",
  redirect: "UmVkaXJlY3RSb3V0aW5nQWN0aW9u",
  holdAndWait: "SG9sZEFuZFdhaXRBY3Rpb24=",
  fraudDetection: "RnJhdWREZWN0ZWN0aW9uQWN0aW9u",
  webhook: "V2ViaG9va1JvdXRpbmdBY3Rpb24=",
  jump: "SnVtcFJvdXRpbmdBY3Rpb24=",
  smartRecurringSetupCit: "U21hcnRSZWN1cnJpbmdTZXR1cENpdFJvdXRpbmdBY3Rpb24=",
  smartRecurringProcessingMit:
    "U21hcnRSZWN1cnJpbmdQcm9jZXNzaW5nTWl0Um91dGluZ0FjdGlvbg==",
  defaultSettings: "RGVmYXVsdFJvdXRpbmdBY3Rpb24=",
} as const;

export type BEActionResourceType =
  (typeof ActionResourceType)[keyof typeof ActionResourceType];
export type FEActionResourceType = keyof typeof ActionResourceType;

export const TaskResourceType = {
  webhook: "V2ViaG9va1JvdXRpbmdBY3Rpb24=",
} as const;

export const RESOURCE_TYPES = {
  ...TriggerResourceType,
  conditions: { ...ConditionResourceType },
  actions: {
    ...ActionResourceType,
  },
  tasks: {
    ...TaskResourceType,
  },
};

type ConditionsConfig = {
  [K in keyof typeof ConditionResourceType as (typeof ConditionResourceType)[K]]: {
    resourceType: K;
    label: string;
    type?: string;
  };
};

/**
 * Resources client mapping
 */
export const CONDITIONS_CONFIG: ConditionsConfig = {
  [ConditionResourceType.currency]: {
    resourceType: "currency",
    label: "Currency",
    type: "dropdown",
  },
  [ConditionResourceType.amount]: {
    resourceType: "amount",
    label: "Amount",
    type: "number",
  },
  [ConditionResourceType.customCondition]: {
    resourceType: "customCondition",
    label: "Custom field",
    type: "text",
  },
  [ConditionResourceType.country]: {
    resourceType: "country",
    label: "Country",
    type: "dropdown",
  },
  [ConditionResourceType.error]: {
    resourceType: "error",
    label: "Error",
    type: "dropdown",
  },
  [ConditionResourceType.binCode]: {
    resourceType: "binCode",
    label: "BIN Code",
  },
  [ConditionResourceType.cardBrand]: {
    resourceType: "cardBrand",
    label: "Card Brand",
  },
  [ConditionResourceType.cardCountry]: {
    resourceType: "cardCountry",
    label: "Card Country",
  },
  [ConditionResourceType.cardType]: {
    resourceType: "cardType",
    label: "Card Type",
  },
  [ConditionResourceType.metaData]: {
    resourceType: "metaData",
    label: "Metadata",
  },
  [ConditionResourceType.riskLevel]: {
    resourceType: "riskLevel",
    label: "Risk Level",
    type: "dropdown",
  },
  [ConditionResourceType.declineType]: {
    resourceType: "declineType",
    label: "Decline type",
  },
  [ConditionResourceType.http]: {
    resourceType: "http",
    label: "HTTP Condition",
  },
  [ConditionResourceType.methods]: {
    resourceType: "methods",
    label: "Selected Method",
  },

  [ConditionResourceType.merchantInitiated]: {
    resourceType: "merchantInitiated",
    label: "Payment initiation",
  },
  [ConditionResourceType.tokenPresent]: {
    resourceType: "tokenPresent",
    label: "Token present",
  },
  [ConditionResourceType.paymentType]: {
    resourceType: "paymentType",
    label: "Payment Type",
  },
  [ConditionResourceType.walletNumbers]: {
    resourceType: "walletNumbers",
    label: "STCPay Wallet Numbers",
  },
};

type ActionsConfig = {
  [K in keyof typeof ActionResourceType as (typeof ActionResourceType)[K]]: {
    resourceType: K;
    label: string;
    // migrate old labels to new ones for MH terminologies
    oldLabel?: string;
    Icon:
      | ((props: React.SVGProps<SVGSVGElement>) => JSX.Element)
      | ((node: Node) => (props: React.SVGProps<SVGSVGElement>) => JSX.Element);
    SidePanelIcon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
    color: string;
    description: string;
  };
};

export const ACTIONS_CONFIG: ActionsConfig = {
  [ActionResourceType.provider]: {
    resourceType: "provider",
    label: "Payment Provider",
    description: "Add a payment provider",
    Icon: PaymentProviderIcon,
    SidePanelIcon: PaymentProviderIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.smartRecurringSetupCit]: {
    resourceType: "smartRecurringSetupCit",
    label: "Smart Recurring Setup (CIT)",
    description:
      "Maximize agreement ID coverage with every CIT. Moneyhash identifies uncovered providers and routes transactions accordingly, ensuring smoother recurring processing.",
    Icon: RefreshIcon,
    SidePanelIcon: RefreshIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.smartRecurringProcessingMit]: {
    resourceType: "smartRecurringProcessingMit",
    label: "Smart Recurring Processing (MIT)",
    description:
      "Automatically identify covered providers and route each MIT transaction to the one with optimal agreement ID coverage for seamless processing.",
    Icon: RefreshIcon,
    SidePanelIcon: RefreshIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.capture]: {
    resourceType: "capture",
    oldLabel: "Capture Payment",
    label: "Capture Transaction",
    description: "Capture an authorized tranasction",
    Icon: PayInProviderIcon,
    SidePanelIcon: PayInProviderIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.void]: {
    resourceType: "void",
    oldLabel: "Void Payment",
    label: "Void Transaction",
    description: "Void the transaction",
    Icon: VoidPaymentIcon,
    SidePanelIcon: VoidPaymentIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.block]: {
    resourceType: "block",
    oldLabel: "Block Payment",
    label: "Block Transaction",
    description: "Blocks the transaction from being processed",
    Icon: XIcon,
    SidePanelIcon: XIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.close]: {
    resourceType: "close",
    oldLabel: "Close Payment",
    label: "Close Intent",
    description: "Mark the intent as closed and send webhook update",
    Icon: BanIcon,
    SidePanelIcon: BanIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.refund]: {
    resourceType: "refund",
    oldLabel: "Refund Payment",
    label: "Refund Transaction",
    description: "Refund the transaction",
    Icon: ReceiptRefundIcon,
    SidePanelIcon: ReceiptRefundIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.branch]: {
    resourceType: "branch",
    label: "Conditional",
    description: "Split into branches based on conditions",
    Icon: Data2Icon,
    SidePanelIcon: Data2Icon,
    color: "text-success",
  },
  [ActionResourceType.recovery]: {
    resourceType: "recovery",
    label: "Recovery Rule",
    description:
      "Retry payment, add a fallback provider, or suggest another payment method",
    color: "text-teal-500",
    Icon: (node?: Node) => {
      const recoveryType = node?.data?.appliedAction?.data?.recovery_type;
      if (recoveryType === "SuggestMethod") return ConvertCardIcon;
      if (recoveryType === "Fallback") return OptimizationIcon;
      if (recoveryType === "Retry") return RefreshIcon;
      return OptimizationIcon;
    },
    SidePanelIcon: OptimizationIcon,
  },
  [ActionResourceType.delay]: {
    resourceType: "delay",
    label: "Delay",
    description:
      "Wait for a defined amount of time before performing an action",
    Icon: ClockIcon,
    SidePanelIcon: ClockIcon,
    color: "text-teal-500",
  },
  [ActionResourceType.transition]: {
    resourceType: "transition",
    label: "Transition",
    Icon: RefreshIcon,
    SidePanelIcon: RefreshIcon,
    description: "",
    color: "text-success",
  },
  [ActionResourceType.redirect]: {
    resourceType: "redirect",
    label: "Redirect",
    description: "Initiate a redirection to an external page",
    Icon: ExternalLinkIcon,
    SidePanelIcon: ExternalLinkIcon,
    color: "text-purple-500",
  },
  [ActionResourceType.holdAndWait]: {
    resourceType: "holdAndWait",
    label: "Hold & Wait",
    description: "Hold intent execution until resume action is initiated",
    Icon: PauseIcon,
    SidePanelIcon: PauseIcon,
    color: "text-purple-500",
  },
  [ActionResourceType.fraudDetection]: {
    resourceType: "fraudDetection",
    label: "Fraud Detection",
    description: "Add a fraud detection provider",
    Icon: SecurityCardIcon,
    SidePanelIcon: SecurityCardIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.deviceCheck]: {
    resourceType: "deviceCheck",
    label: "Device check",
    description: "Add a device check provider",
    Icon: SecurityCardIcon,
    SidePanelIcon: SecurityCardIcon,
    color: "text-[theme(borderColor.accent-bold)]",
  },
  [ActionResourceType.webhook]: {
    resourceType: "webhook",
    label: "Webhook",
    description: "Send a custom webhook to an external link",
    Icon: CloudIcon,
    SidePanelIcon: CloudIcon,
    color: "text-purple-500",
  },
  [ActionResourceType.jump]: {
    resourceType: "jump",
    label: "Jump",
    color: "text-success -scale-x-100",
    description: "Jump to another sequence in your flow and avoid duplication",
    Icon: ReplyIcon,
    SidePanelIcon: ReplyIcon,
  },
  [ActionResourceType.defaultSettings]: {
    resourceType: "defaultSettings",
    label: "Payment Defaults",
    oldLabel: "Action",
    Icon: CollectionIcon,
    SidePanelIcon: CollectionIcon, // will not be used in side panel
    description:
      "Configuration for default payment method when no trigger is met",
    color: "text-[theme(backgroundColor.chart-accent-3)]",
  },
};

export const requiredTriggerConditions: FEConditionResourceType[] = [
  "currency",
  "merchantInitiated",
  "tokenPresent",
];
